import { useRef, useEffect } from 'react';

const AudioVisualization = ({ isRecording, isPaused }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);

  // Initialize audio context and analyser
  useEffect(() => {
    if (!isRecording || isPaused) return;

    const setupAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 2048;
        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;

        drawWaveform();
      } catch (err) {
        console.error('Error accessing microphone:', err);
      }
    };

    setupAudio();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [isRecording, isPaused]);

  // Custom squiggly waveform render function
  const drawWaveform = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    let lastFrameTime = 0;
    const frameDelay = 1000 / 20; // Limit to 20 FPS for smoother animation

    const render = (currentTime) => {
      animationRef.current = requestAnimationFrame(render);

      // Throttle the animation to 20 FPS
      if (currentTime - lastFrameTime < frameDelay) {
        return;
      }
      lastFrameTime = currentTime;

      analyser.getByteTimeDomainData(dataArray);

      const { width, height } = canvas;
      const scale = dataArray.length / width;
      const step = 10;

      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Draw squiggly waveform
      ctx.save();
      ctx.translate(0, height / 2);
      ctx.strokeStyle = '#10b981'; // Green color
      ctx.fillStyle = '#10b981';
      ctx.lineWidth = 2;
      ctx.beginPath();

      for (let i = 0; i < width; i += step * 2) {
        const index = Math.floor(i * scale);
        const value = Math.abs((dataArray[index] - 128) / 128.0);
        let x = i;
        let y = value * height * 1.5; // Increased amplitude from 0.8 to 1.5

        ctx.moveTo(x, 0);
        ctx.lineTo(x, y);
        ctx.arc(x + step / 2, y, step / 2, Math.PI, 0, true);
        ctx.lineTo(x + step, 0);

        x = x + step;
        y = -y;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, y);
        ctx.arc(x + step / 2, y, step / 2, Math.PI, 0, false);
        ctx.lineTo(x + step, 0);
      }

      ctx.stroke();
      ctx.closePath();
      ctx.restore();
    };

    render(0);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      minHeight: '280px',
      background: 'white',
      borderRadius: '12px',
      margin: '20px',
      border: '1px solid #e5e7eb'
    }}>
      {/* Status indicator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: isRecording && !isPaused ? '#059669' : isPaused ? '#f59e0b' : '#6b7280',
          animation: isRecording && !isPaused ? 'pulse 2s infinite' : 'none'
        }} />
        <span style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#111827'
        }}>
          {isRecording && !isPaused ? 'Recording in progress...' : isPaused ? 'Paused' : 'Waiting'}
        </span>
      </div>

      {/* Waveform visualization */}
      <div style={{
        background: '#f9fafb',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '20px'
      }}>
        <canvas
          ref={canvasRef}
          width="800"
          height="200"
          style={{
            display: 'block',
            maxWidth: '100%',
            height: 'auto'
          }}
        />
      </div>

      {/* Helpful text */}
      <p style={{
        fontSize: '14px',
        color: '#6b7280',
        textAlign: 'center',
        maxWidth: '600px',
        lineHeight: '1.6',
        fontWeight: '500'
      }}>
        {isRecording && !isPaused
          ? 'Transcription is in progress. Speak normally; your words are captured in real time.'
          : isPaused
          ? 'Recording is paused. Click the button to resume.'
          : 'Click "Start" in the sidebar to begin recording.'
        }
      </p>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );
};

export default AudioVisualization;
